import { fromJS, is, List, Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import {
  isChecked,
  isJSON,
  isLength,
  isNotEmpty,
  optional,
  validateRecord,
} from '@gonfalon/legacy-form-validation/validationUtils';
import type { CreateFunctionInput, ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { isEmpty } from 'lodash';

import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { AccessChecks } from '#utils/access/types';
import type { Link } from '#utils/linkUtils';
import type { Member } from '#utils/members/records/Member';
import type { PolicyStatement } from '#utils/policyUtils';
import { isPolicyString } from '#utils/policyUtils';
import { isAbsoluteURL, isValidURLPort } from '#utils/urlUtils';

type SlackIncomingHookProps = {
  _access: AccessChecks | null;
  _links: ImmutableMap<{
    self: Link;
  }>;
  _id: string;
  name: string;
  url: string;
  statements: PolicyStatement[] | null;
  on: boolean;
  tags: List<string>;
};

export class SlackIncomingHook extends Record<SlackIncomingHookProps>({
  _access: null,
  _links: Map(),
  _id: '',
  name: '',
  url: '',
  statements: null,
  on: true,
  tags: List(),
}) {
  checkAccess({ profile }: { profile: Member }) {
    const access = this.get('_access');

    if (profile.isReader()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Reader' });
    }

    if (profile.isAdmin() || profile.isWriter() || profile.isOwner() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action: string) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }
  selfLink() {
    return this._links.getIn(['self', 'href']);
  }

  toForm() {
    return createSlackIncomingHookForm({
      name: this.name,
      url: this.url,
      statements: this.statements ? JSON.stringify(this.statements, null, 2) : '',
      on: this.on,
      tags: this.tags,
      // We only want to get privacy policy approval for new subscriptions. If we already have a subscription
      // then we can assume the privacy policy has been approved
      privacyPolicyApproved: true,
    });
  }

  mergeForm(form: SlackIncomingHookForm) {
    return this.withMutations((hook) =>
      hook.merge({
        name: form.name,
        url: form.url,
        statements: isEmpty(form.statements) ? null : JSON.parse(form.statements),
        on: form.on,
        tags: form.tags,
      }),
    );
  }
}

export function createSlackIncomingHook(props: CreateFunctionInput<SlackIncomingHook> = {}) {
  return props instanceof SlackIncomingHook ? props : new SlackIncomingHook(fromJS(props));
}

type SlackIncomingHookFormProps = {
  name: string;
  url: string;
  statements: string;
  on: boolean;
  tags: List<string>;
  privacyPolicyApproved: boolean;
};

export class SlackIncomingHookForm extends Record<SlackIncomingHookFormProps>({
  name: '',
  url: '',
  statements: '',
  on: true,
  tags: List(),
  privacyPolicyApproved: false,
}) {
  validate() {
    return validateRecord(
      this,
      isChecked('privacyPolicyApproved'),
      optional(isNotEmpty)('name'),
      isLength(0, 100)('name'),
      isNotEmpty('url'),
      isAbsoluteURL('url'),
      isValidURLPort('url'),
      optional(isJSON)('statements'),
      optional(isPolicyString)('statements'),
    );
  }

  is(other: SlackIncomingHookForm) {
    try {
      return (
        this.name === other.name &&
        this.url === other.url &&
        ((isEmpty(this.statements) && isEmpty(other.statements)) ||
          is(fromJS(JSON.parse(this.statements)), fromJS(JSON.parse(other.statements)))) &&
        this.on === other.on &&
        is(this.tags, other.tags)
      );
    } catch {
      // could fail to parse as JSON
      return false;
    }
  }

  toSlackIncomingHook() {
    const statements = this.statements !== '' ? JSON.parse(this.statements) : null;
    return createSlackIncomingHook({
      name: this.name,
      url: this.url,
      statements: statements && statements.length ? statements : null,
      on: this.on,
      tags: this.tags,
    });
  }
}

export function createSlackIncomingHookForm(props: CreateFunctionInput<SlackIncomingHookForm> = {}) {
  return props instanceof SlackIncomingHookForm ? props : new SlackIncomingHookForm(fromJS(props));
}
